import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, useMap } from "react-leaflet";
import type { LatLngBoundsExpression } from "leaflet";
import "leaflet/dist/leaflet.css";
import "./driver-map.css";

/**
 * Keeps the map centred on the driver as they move ("follow"), but lets the
 * viewer grab and pan the map freely. The moment they drag, follow is paused
 * (so the map doesn't yank back under their finger); a "Follow driver" button
 * appears to re-enable it. Zooming does not break follow — it keeps the centre.
 */
function DriverFollower({
  lat, lng, following, onUserPan,
}: {
  lat: number; lng: number; following: boolean; onUserPan: () => void;
}) {
  const map = useMap();
  const isFirst = useRef(true);

  // A manual drag pauses follow. (Programmatic panTo below doesn't fire dragstart.)
  useEffect(() => {
    const handle = () => onUserPan();
    map.on("dragstart", handle);
    return () => { map.off("dragstart", handle); };
  }, [map, onUserPan]);

  // Follow the driver on each new position (and immediately when follow is re-enabled).
  useEffect(() => {
    if (isFirst.current) { isFirst.current = false; return; }
    if (following) map.panTo([lat, lng], { animate: true, duration: 1.0 });
  }, [lat, lng, following, map]);

  return null;
}

function MapBoundsFitter({
  driverLat, driverLng, storeLat, storeLng,
}: {
  driverLat: number; driverLng: number; storeLat: number; storeLng: number;
}) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current) return;
    fitted.current = true;
    const bounds: LatLngBoundsExpression = [
      [driverLat, driverLng],
      [storeLat, storeLng],
    ];
    map.fitBounds(bounds, { padding: [28, 28], maxZoom: 16 });
  }, []);

  return null;
}

export default function DriverLocationMap({
  lat,
  lng,
  storeLat,
  storeLng,
  height = 200,
}: {
  lat: number;
  lng: number;
  storeLat?: number | null;
  storeLng?: number | null;
  height?: number;
}) {
  const hasStore = storeLat != null && storeLng != null;
  const [following, setFollowing] = useState(true);

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <MapContainer
        center={[lat, lng]}
        zoom={15}
        style={{ width: "100%", height }}
        scrollWheelZoom
        dragging
        zoomControl
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {hasStore && (
          <CircleMarker
            center={[storeLat!, storeLng!]}
            radius={9}
            pathOptions={{ fillColor: "#059669", color: "#fff", weight: 2, fillOpacity: 1 }}
          />
        )}
        <CircleMarker
          center={[lat, lng]}
          radius={10}
          pathOptions={{ fillColor: "#1d4ed8", color: "#fff", weight: 2.5, fillOpacity: 1 }}
        />
        {hasStore && (
          <MapBoundsFitter
            driverLat={lat}
            driverLng={lng}
            storeLat={storeLat!}
            storeLng={storeLng!}
          />
        )}
        <DriverFollower lat={lat} lng={lng} following={following} onUserPan={() => setFollowing(false)} />
      </MapContainer>

      {!following && (
        <button
          type="button"
          onClick={() => setFollowing(true)}
          style={{
            position: "absolute", bottom: 10, left: "50%", transform: "translateX(-50%)",
            zIndex: 1000, display: "flex", alignItems: "center", gap: 6,
            background: "#1d4ed8", color: "#fff", border: "none",
            borderRadius: 9999, padding: "6px 14px", fontSize: 12, fontWeight: 600,
            cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#fff", display: "inline-block" }} />
          Follow driver
        </button>
      )}
    </div>
  );
}
